(() => {
  const PRE_BOOK_PROPERTY = '_pre_book';
  const PRE_BOOK_ENDPOINT = '/apps/api/pre-book/orders';

  function t(key, fallback) {
    return window.preBookStrings?.[key] || fallback;
  }

  function isPreBookLine(item) {
    const properties = item?.properties || {};
    return properties[PRE_BOOK_PROPERTY] === 'true' || properties[PRE_BOOK_PROPERTY] === true;
  }

  function getCartState(cart) {
    const items = cart?.items || [];
    const hasPreBook = items.some(isPreBookLine);
    const hasNormal = items.some((item) => !isPreBookLine(item));

    return {
      items,
      hasPreBook,
      hasNormal,
      isMixed: hasPreBook && hasNormal,
      isPreBookOnly: hasPreBook && !hasNormal,
      isNormalOnly: hasNormal && !hasPreBook,
    };
  }

  async function fetchCart() {
    const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(t('fetchCartError', 'Unable to fetch cart'));
    return response.json();
  }

  function getPreBookForm(element) {
    return element?.closest('product-form');
  }

  function isPreBookProductForm(element) {
    return getPreBookForm(element)?.dataset.preBook === 'true';
  }

  function enrichProductFormData(formData, productForm) {
    if (productForm?.dataset.preBook === 'true') {
      formData.append(`properties[${PRE_BOOK_PROPERTY}]`, 'true');
    }
  }

  async function canAddToCart(isPreBook) {
    const cart = await fetchCart();
    const state = getCartState(cart);

    if (isPreBook && state.hasNormal) {
      return { allowed: false, message: t('mixedCartError', 'Pre-Book items must be ordered separately from regular items.') };
    }

    if (!isPreBook && state.hasPreBook) {
      return { allowed: false, message: t('normalAddBlocked', 'Please complete your Pre-Book order before adding regular items.') };
    }

    return { allowed: true };
  }

  async function canApplyQuickOrderChange(quickOrderList, items) {
    const isPreBook = quickOrderList?.dataset.preBook === 'true';
    const addsItems = Object.values(items || {}).some((quantity) => Number(quantity) > 0);

    if (!addsItems) {
      return { allowed: true };
    }

    return canAddToCart(isPreBook);
  }

  function buildQuickOrderPayload(quickOrderList, body) {
    if (quickOrderList?.dataset.preBook !== 'true') return body;

    const addItems = [];
    const remainingUpdates = {};

    Object.entries(body.updates || {}).forEach(([variantId, quantity]) => {
      const targetQuantity = Number(quantity);
      const row = quickOrderList.querySelector(`#Variant-${variantId}`);
      const currentQuantity = Number(row?.dataset.cartQty || 0);
      const delta = targetQuantity - currentQuantity;

      if (delta > 0) {
        addItems.push({
          id: Number(variantId),
          quantity: delta,
          properties: { [PRE_BOOK_PROPERTY]: 'true' },
        });
      } else if (targetQuantity !== currentQuantity) {
        remainingUpdates[variantId] = targetQuantity;
      }
    });

    return {
      ...body,
      updates: remainingUpdates,
      addItems,
    };
  }

  function setCartError(message) {
    const errors = document.querySelectorAll('#cart-errors, #CartDrawer-CartErrors, [data-pre-book-error]');
    errors.forEach((element) => {
      element.textContent = message || '';
      element.toggleAttribute('hidden', !message);
    });
  }

  function setButtonVisibility(selector, visible) {
    document.querySelectorAll(selector).forEach((element) => {
      element.hidden = !visible;
      element.style.display = visible ? '' : 'none';
    });
  }

  function updateCartButtons(cart) {
    const state = getCartState(cart);
    const checkoutButtons = document.querySelectorAll('#checkout, [name="checkout"]:not([data-pre-book-submit])');
    const preBookButtons = document.querySelectorAll('[data-pre-book-submit]');

    if (state.isPreBookOnly) {
      checkoutButtons.forEach((button) => {
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
        button.hidden = true;
        button.style.display = 'none';
      });
      preBookButtons.forEach((button) => {
        button.disabled = false;
        button.removeAttribute('aria-disabled');
        button.hidden = false;
        button.style.display = '';
      });
      setButtonVisibility('.cart__dynamic-checkout-buttons, .additional-checkout-buttons', false);
      setCartError('');
    } else if (state.isMixed) {
      checkoutButtons.forEach((button) => {
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
        button.hidden = false;
        button.style.display = '';
      });
      preBookButtons.forEach((button) => {
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
        button.hidden = false;
        button.style.display = '';
      });
      setButtonVisibility('.cart__dynamic-checkout-buttons, .additional-checkout-buttons', false);
      setCartError(t('mixedCartError', 'Pre-Book items must be ordered separately from regular items.'));
    } else {
      checkoutButtons.forEach((button) => {
        if (state.items.length > 0) {
          button.disabled = false;
          button.removeAttribute('aria-disabled');
        }
        button.hidden = false;
        button.style.display = '';
      });
      preBookButtons.forEach((button) => {
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
        button.hidden = true;
        button.style.display = 'none';
      });
      setButtonVisibility('.cart__dynamic-checkout-buttons, .additional-checkout-buttons', true);
      setCartError('');
    }

    document.querySelectorAll('[data-pre-book-actions]').forEach((element) => {
      element.hidden = !state.isPreBookOnly;
    });
  }

  async function refreshCartButtons() {
    try {
      updateCartButtons(await fetchCart());
    } catch (error) {
      console.error(error);
    }
  }

  function normalizeCartLineItems(cart) {
    return getCartState(cart).items.map((item) => ({
      variant_id: item.variant_id,
      quantity: item.quantity,
    }));
  }

  function normalizeCartAttributes(cart) {
    return Object.fromEntries(
      Object.entries(cart?.attributes || {}).filter(([, value]) => value !== null && value !== undefined && String(value).trim() !== '')
    );
  }

  async function clearCart() {
    await fetch('/cart/clear.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
    });
  }

  async function submitPreBookOrder(event) {
    event.preventDefault();
    const button = event.currentTarget;
    button.disabled = true;
    button.classList.add('loading');
    button.querySelector('.loading__spinner')?.classList.remove('hidden');
    setCartError('');

    try {
      const cart = await fetchCart();
      const state = getCartState(cart);

      if (!state.isPreBookOnly) {
        throw new Error(
          state.isMixed
            ? t('mixedCartError', 'Pre-Book items must be ordered separately from regular items.')
            : t('noPreBookItems', 'Your cart does not contain Pre-Book items.')
        );
      }

      const response = await fetch(PRE_BOOK_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          line_items: normalizeCartLineItems(cart),
          note: cart.note || undefined,
          attributes: normalizeCartAttributes(cart),
        }),
      });
      const payload = await response.json();

      if (!response.ok || payload.code !== 200) {
        throw new Error(payload.msg || t('submitError', 'Unable to submit Pre-Book order.'));
      }

      await clearCart();
      window.location.href = '/account?pre_book_submitted=1#pre-book-orders';
    } catch (error) {
      setCartError(error instanceof Error ? error.message : t('submitError', 'Unable to submit Pre-Book order.'));
      button.disabled = false;
      button.classList.remove('loading');
      button.querySelector('.loading__spinner')?.classList.add('hidden');
    }
  }

  function displayStatus(order) {
    if (order.status === 'completed') return t('statusConfirmed', 'Confirmed');
    if (order.status === 'cancelled') return t('statusCancelled', 'Cancelled');
    if (order.applied_discount) return t('statusDiscountApplied', 'Discount Applied');
    return t('statusPendingReview', 'Pending Review');
  }

  function formatDate(value) {
    if (!value) return '';
    return new Intl.DateTimeFormat(undefined, { year: 'numeric', month: 'short', day: 'numeric' }).format(new Date(value));
  }

  function renderOrders(container, orders) {
    if (!orders.length) {
      container.innerHTML = `<p>${escapeHtml(
        t('emptyOrders', 'No Pre-Book orders yet. Submitted Pre-Book draft orders will appear here after creation.')
      )}</p>`;
      return;
    }

    const rows = orders
      .map(
        (order) => `
          <tr>
            <td data-label="Draft">${escapeHtml(order.name)}</td>
            <td data-label="Date">${formatDate(order.created_at)}</td>
            <td data-label="Status">${displayStatus(order)}</td>
            <td data-label="Subtotal">${escapeHtml(order.subtotal_price)}</td>
            <td data-label="Discount">${
              order.applied_discount ? escapeHtml(order.applied_discount.title || t('discountApplied', 'Applied')) : '-'
            }</td>
            <td data-label="Total">${escapeHtml(order.total_price)}</td>
          </tr>
        `
      )
      .join('');

    container.innerHTML = `
      <table role="table" class="order-history">
        <thead>
          <tr>
            <th>${escapeHtml(t('tableDraft', 'Draft'))}</th>
            <th>${escapeHtml(t('tableDate', 'Date'))}</th>
            <th>${escapeHtml(t('tableStatus', 'Status'))}</th>
            <th>${escapeHtml(t('tableSubtotal', 'Subtotal'))}</th>
            <th>${escapeHtml(t('tableDiscount', 'Discount'))}</th>
            <th>${escapeHtml(t('tableTotal', 'Total'))}</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
    `;
  }

  function escapeHtml(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  async function loadPreBookOrders() {
    const container = document.querySelector('[data-pre-book-orders]');
    if (!container) return;

    const success = document.querySelector('[data-pre-book-success]');
    if (success) {
      const params = new URLSearchParams(window.location.search);
      success.hidden = params.get('pre_book_submitted') !== '1';
    }

    try {
      const response = await fetch(PRE_BOOK_ENDPOINT, { headers: { Accept: 'application/json' } });
      const payload = await response.json();
      if (!response.ok || payload.code !== 200) {
        throw new Error(payload.msg || t('loadError', 'Unable to load Pre-Book orders.'));
      }
      renderOrders(container, payload.data?.orders || []);
    } catch (error) {
      container.innerHTML = `<p>${escapeHtml(error instanceof Error ? error.message : t('loadError', 'Unable to load Pre-Book orders.'))}</p>`;
    }
  }

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[data-pre-book-submit]').forEach((button) => {
      button.addEventListener('click', submitPreBookOrder);
    });
    refreshCartButtons();
    loadPreBookOrders();
  });

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.cartData) {
        updateCartButtons(event.cartData);
      } else {
        refreshCartButtons();
      }
    });
  }

  window.PreBook = {
    propertyName: PRE_BOOK_PROPERTY,
    isPreBookProductForm,
    enrichProductFormData,
    canAddToCart,
    canApplyQuickOrderChange,
    buildQuickOrderPayload,
    refreshCartButtons,
  };
})();
